import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MagnifyingGlass, ChatCircleDots, User, SignOut, List } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';
import DropioLogo from '@/components/DropioLogo';

const Navigation = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" data-testid="nav-logo">
            <DropioLogo size={32} />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/search"
              className="flex items-center gap-2 text-gray-600 hover:text-[#6D28D9] transition-colors duration-200 font-medium"
              data-testid="nav-search"
            >
              <MagnifyingGlass size={20} weight="bold" />
              <span>Find Travelers</span>
            </Link>
            <Link
              to="/messages"
              className="flex items-center gap-2 text-gray-600 hover:text-[#6D28D9] transition-colors duration-200 font-medium"
              data-testid="nav-messages"
            >
              <ChatCircleDots size={20} weight="bold" />
              <span>Messages</span>
            </Link>
            {user && (
              <Link
                to={`/profile/${user.user_id}`}
                className="flex items-center gap-2 text-gray-600 hover:text-[#6D28D9] transition-colors duration-200 font-medium"
                data-testid="nav-profile"
              >
                <User size={20} weight="bold" />
                <span>Profile</span>
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors duration-200 font-medium"
              data-testid="nav-logout"
            >
              <SignOut size={20} weight="bold" />
              <span>Logout</span>
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-50"
            data-testid="mobile-menu-toggle"
          >
            <List size={24} weight="bold" className="text-gray-900" />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-gray-200">
            <Link
              to="/search"
              className="block px-4 py-2 text-gray-700 hover:text-[#6D28D9] hover:bg-gray-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              Find Travelers
            </Link>
            <Link
              to="/messages"
              className="block px-4 py-2 text-gray-700 hover:text-[#6D28D9] hover:bg-gray-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              Messages
            </Link>
            {user && (
              <Link
                to={`/profile/${user.user_id}`}
                className="block px-4 py-2 text-gray-700 hover:text-[#6D28D9] hover:bg-gray-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Profile
              </Link>
            )}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
